import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, TextField, Button, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  CircularProgress, Alert, Tooltip, Chip, IconButton, Pagination,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox,
  InputAdornment, useMediaQuery, Divider, Menu,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Description as ReportIcon, CheckCircle as PaidIcon,
  Schedule as CreditIcon, Cancel as NotEatenIcon,
  Groups as PeopleIcon, Layers as ValueIcon,
  Print as PrintIcon, GridOn as ExcelIcon,
  Autorenew as GenerateIcon, PersonAdd as AddPersonIcon,
  Info as InfoIcon, ChevronLeft, ChevronRight,
  Today as TodayIcon, Block as RejectIcon,
  Search as SearchIcon, DoneAll as ApproveIcon,
  ArrowDropDown as CaretIcon,
  MoneyOff as WriteOffIcon, PendingActions as PendingIcon,
} from '@mui/icons-material';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import API from '../../BaseURL';
import swal from '../../utils/swal';
import CorrectCellDialog from './CorrectCellDialog';
import PrepaymentDialog from './PrepaymentDialog';
// Writing a balance off moves the same money approving a payment does,
// so it is gated on the same permission.
import { useAuth } from '../../context/AuthContext';

// ═══════════════════════════════════════════════════════════════════
//  The collection report an approver works from.
//
//  The flat pending list answers "what is waiting?" and nothing else.
//  It cannot answer the questions somebody signing off money actually
//  has — did this child eat on Tuesday? is this the third day running
//  they have not paid? did the whole class go unmarked on Wednesday? —
//  because one learner's week is scattered down five pages between
//  other people's rows.
//
//  So: ONE CLASS, ONE COLLECTION, ONE WEEK, as a grid. A row per
//  learner, a column per day. It is the shape of the paper register it
//  replaces, and the shape the school already reads.
//
//  Approving happens ON the grid — a day, a selection, or the whole
//  week — through the same approveBulk pipeline the list used, so
//  nothing about how money posts has changed. Yesterday's round is
//  checked and cleared this morning, from its own column.
// ═══════════════════════════════════════════════════════════════════

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const money = (n) => Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const prettyType = (t) => String(t || '')
  .replace(/_+$/, '').replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
                   + `-${String(d.getDate()).padStart(2, '0')}`;

// The Monday of whatever week a date falls in. Sunday counts as the end
// of the week that has just gone, not the start of the next one.
const mondayOf = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  d.setHours(0, 0, 0, 0);
  return d;
};
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const daysBetween = (a, b) =>
  Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 86400000);

const dayLabel = (d) => {
  const x = new Date(`${d}T00:00:00`);
  return {
    dow: x.toLocaleDateString(undefined, { weekday: 'short' }),
    dm:  x.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
  };
};
const longDate = (d) => new Date(`${d}T00:00:00`)
  .toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });

const rangeLabel = (from, to) =>
  from === to ? longDate(from) : `${longDate(from)} – ${longDate(to)}`;

// A report longer than a week stops being a week sheet: the columns no
// longer fit a page and the eye cannot hold the row. Seven days is the
// hard ceiling.
const MAX_DAYS = 7;

// ── How one cell reads ─────────────────────────────────────────────
//
// The teacher's own four words in the teacher's own four colours, so a
// supervisor checking their work is reading the same sheet they filled
// in. A fifth state — nothing marked at all — is the one that matters
// most here and is the easiest to miss, so it gets its own grey rather
// than an empty box.
const CELL = {
  full:    { bg: '#e8f6ed', fg: '#15803d', line: '#b9e3ca', word: 'PAID' },
  partial: { bg: '#eaf2fb', fg: '#1c5b96', line: '#c6dcf1', word: 'PART' },
  none:    { bg: '#fdf2e0', fg: '#a1610a', line: '#f3ddb8', word: 'CREDIT' },
  absent:  { bg: '#fdeaea', fg: '#b91c1c', line: '#f6cccc', word: 'NOT EATEN' },
  // Paid before the day came round. Its own colour, because it is
  // neither "collected today" nor "owed" — the money is already in.
  advance: { bg: '#eef0fe', fg: '#4338ca', line: '#c7cffb', word: 'ADVANCE' },
};
const BLANK = { bg: '#f1f5f9', fg: '#94a3b8', line: '#e2e8f0', word: '—' };

// The same two decisions the Cell makes, for the printed and exported
// copies — so the paper can never say something the screen does not.
const settledFromAdvance = (c) => !!c && c.status !== 'absent'
  && Number(c.credit_used || 0) > 0
  && (Number(c.daily_amount || 0) - Number(c.amount_paid || 0)
      - Number(c.credit_used || 0)) <= 0.009;

const cellWord = (c) => settledFromAdvance(c)
  ? CELL.advance.word : (CELL[c.status] || BLANK).word;

const cellAmount = (c) => settledFromAdvance(c)
  ? Number(c.credit_used || 0)
  : (c.status === 'absent' || c.status === 'none' ? 0 : Number(c.amount_paid || 0));

// Where a learner's whole range lands. Exactly one applies, so the
// counts underneath add up to the class.
const STATUS_LABEL = {
  paid:     { word: 'Fully Paid', bg: '#dff3e7', fg: '#15803d' },
  credit:   { word: 'On Credit',  bg: '#fdf0d8', fg: '#a1610a' },
  noteaten: { word: 'Not Eaten',  bg: '#fbe0e0', fg: '#b91c1c' },
  none:     { word: 'Not marked', bg: '#eef2f7', fg: '#64748b' },
};

// Approval state is shown BY EXCEPTION, and only in the cell's own
// outline — never as a second colour laid over it.
//
// A bar under every cell plus a dot on top of it drew a yellow stripe
// through the whole sheet, which is the state of ALMOST EVERY cell
// before an approver starts work: marking the norm marks nothing. So
// approved — the resting state — is left plain, and only the states
// that want a decision are called out.
const OUTLINE = {
  // Still with the teacher. A DASHED edge, because a dashed edge reads
  // as "not finished" in every sheet anyone has ever used — and it is
  // the one state an approver must be able to tell apart from approved
  // at a glance, since both are silent.
  DRAFT:    { line: '#94a3b8', dot: null, style: 'dashed' },
  PENDING:  { line: '#e0901c', dot: '#f0a12e', style: 'solid' },
  REJECTED: { line: '#e13b3b', dot: '#e13b3b', style: 'solid' },
  REVERSED: { line: '#b6c2d0', dot: null, style: 'solid' },
};

const STATE_WORD = {
  DRAFT:    'Not submitted yet — the teacher is still on this round',
  PENDING:  'Submitted · waiting for your approval',
  APPROVED: 'Approved · posted',
  REJECTED: 'Rejected',
  REVERSED: 'Reversed',
};

function Cell({ mark, selected, selectable, onToggle, advance, advanceNote, onCorrect }) {
  // ── A day already paid for, one way or the other ────────────────
  //
  // Two shapes of the same thing. `advance` is a day still to come that
  // money on account already covers — nothing is marked, but it is paid.
  // `settled` is a day the teacher marked CREDIT or part-paid where the
  // learner's advance made up the difference: the teacher was right that
  // no cash changed hands, and the system was right that nothing is
  // owed. Showing that as "CREDIT 0.00" beside a green "Fully Paid" was
  // the sheet arguing with itself.
  const settled = !!mark && mark.status !== 'absent'
    && Number(mark.credit_used || 0) > 0
    && (Number(mark.daily_amount || 0) - Number(mark.amount_paid || 0)
        - Number(mark.credit_used || 0)) <= 0.009;

  const c = advance ? CELL.advance
    : !mark ? BLANK
    : settled ? CELL.advance
    : (CELL[mark.status] || BLANK);

  const state = String(mark?.approval_status || '').toUpperCase();
  const flag = mark ? OUTLINE[state] : null;

  const amount = advance ? advance
    : !mark ? null
    : settled ? Number(mark.credit_used || 0)
    : (mark.status === 'absent' || mark.status === 'none' ? 0 : mark.amount_paid);

  const title = advance
    ? `Already paid — GH¢ ${money(advance)} taken from money on account`
      + (advanceNote ? ` · ${advanceNote}` : '')
    : !mark ? 'Nothing was marked for this day'
    : settled
      ? `Covered by advance — GH¢ ${money(mark.credit_used)} from money already paid`
        + (Number(mark.amount_paid || 0) > 0 ? ` plus GH¢ ${money(mark.amount_paid)} on the day` : '')
        + ` · ${STATE_WORD[state] || 'no cash taken on the day'}`
      : `${c.word} · GH¢ ${money(amount)}`
        + ` · ${STATE_WORD[state] || 'No money on this entry'}`
        + (mark.collector_name ? ` · by ${mark.collector_name}` : '')
        + (mark.notes ? ` · ${mark.notes}` : '');

  return (
    <Tooltip title={onCorrect ? `${title} · right-click to correct` : title}>
      <Box
        onClick={selectable ? onToggle : undefined}
        // Correcting is deliberately NOT the left click. A tap picks a
        // pending cell for approval; moving posted money has to be a
        // different gesture, or an approver ticking down a column will
        // eventually open a cash-book movement by accident.
        onContextMenu={onCorrect
          ? (e) => { e.preventDefault(); onCorrect(); }
          : undefined}
        sx={{
          position: 'relative', px: 0.4, py: 0.25,
          borderRadius: 1.25, textAlign: 'center', minWidth: 58,
          bgcolor: c.bg,
          border: '1px solid',
          borderStyle: selected ? 'solid' : (flag?.style || 'solid'),
          borderColor: selected ? '#1e3a6b' : (flag?.line || c.line),
          boxShadow: selected ? '0 0 0 2px rgba(30,58,107,.18)' : 'none',
          cursor: selectable ? 'pointer' : (onCorrect ? 'context-menu' : 'default'),
          '&:hover': (selectable || onCorrect) ? { borderColor: '#1e3a6b' } : {},
        }}>
        <Typography sx={{ fontSize: 8, fontWeight: 800, color: c.fg, lineHeight: 1.25 }}>
          {c.word}
        </Typography>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: c.fg, lineHeight: 1.25, ...TNUM }}>
          {amount == null ? '' : money(amount)}
        </Typography>
        {flag?.dot && (
          <Box sx={{
            position: 'absolute', top: 2, right: 2,
            width: 5, height: 5, borderRadius: '50%', bgcolor: flag.dot,
          }} />
        )}
      </Box>
    </Tooltip>
  );
}

// ── A headline figure at the top ───────────────────────────────────
function StatCard({ icon, tone, label, value, sub }) {
  return (
    <Card elevation={0} sx={{
      px: 1.8, py: 1.6, borderRadius: 3, flex: '1 1 190px', minWidth: 176,
      bgcolor: tone.bg, border: `1px solid ${tone.line}`,
    }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box sx={{
          width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
          bgcolor: tone.chip, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography noWrap sx={{
            fontSize: 12, fontWeight: 700, color: tone.fg, lineHeight: 1.5,
          }}>
            {label}
          </Typography>
          <Typography sx={{
            fontSize: 24, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, ...TNUM,
          }}>
            {value}
          </Typography>
          {sub && (
            <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: tone.sub, ...TNUM }}>
              {sub}
            </Typography>
          )}
        </Box>
      </Stack>
    </Card>
  );
}

// ── The donut ──────────────────────────────────────────────────────
// Drawn with two SVG circles and a dash offset. A charting library for
// three numbers would be 300KB the teacher's phone has to download.
function Donut({ slices, total, caption }) {
  const R = 52, C = 2 * Math.PI * R;
  let offset = 0;
  return (
    <Box sx={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
      <svg width="140" height="140" viewBox="0 0 140 140">
        <circle cx="70" cy="70" r={R} fill="none" stroke="#eef2f7" strokeWidth="18" />
        {total > 0 && slices.filter(s => s.value > 0).map((s) => {
          const len = (s.value / total) * C;
          const el = (
            <circle key={s.label} cx="70" cy="70" r={R} fill="none"
              stroke={s.colour} strokeWidth="18"
              strokeDasharray={`${len} ${C - len}`}
              strokeDashoffset={-offset}
              transform="rotate(-90 70 70)" />
          );
          offset += len;
          return el;
        })}
      </svg>
      <Box sx={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <Typography sx={{ fontSize: 26, fontWeight: 800, color: '#0f172a', lineHeight: 1, ...TNUM }}>
          {total}
        </Typography>
        <Typography sx={{ fontSize: 10.5, fontWeight: 600, color: '#64748b' }}>
          {caption}
        </Typography>
      </Box>
    </Box>
  );
}

// Fifteen learners, not ten. With the rows tightened a page of fifteen
// fits the same screen ten used to, and a class of twenty-five is then
// two pages instead of three.
const PAGE_SIZE = 15;

export default function CollectorWeekSheetPage() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const canApprove = user?.role === 'super_admin'
    || (user?.approvals || []).includes('collection.approve');

  // The range the user is EDITING, and the range last generated. They
  // are separate so typing a date does not fire a query per keystroke —
  // "Generate Report" is what commits it.
  const [draftFrom, setDraftFrom] = useState(() => isoOf(mondayOf(new Date())));
  const [draftTo,   setDraftTo]   = useState(() => isoOf(addDays(mondayOf(new Date()), 4)));
  const [range, setRange] = useState(() => ({
    from: isoOf(mondayOf(new Date())),
    to:   isoOf(addDays(mondayOf(new Date()), 4)),
  }));

  const [className, setClassName] = useState('');
  const [incomeType, setIncomeType] = useState('');
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState([]);
  const [working, setWorking] = useState(false);
  const [page, setPage] = useState(1);
  const [addOpen, setAddOpen] = useState(false);
  const [exportAnchor, setExportAnchor] = useState(null);

  // ── Writing a balance off ───────────────────────────────────────
  // A teacher marks the wrong learner and a debt appears that nobody
  // owes; or the same slip the other way leaves credit the school never
  // received. `writeOffFor` is the learner whose balance is being
  // corrected, null when the dialog is shut.
  const [writeOffFor, setWriteOffFor] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);

  // ── Everything still waiting, wherever it is ────────────────────
  //
  // The sheet shows one class and one week. Entries that were never
  // approved do not go anywhere — they just stop being in the week
  // somebody happens to be looking at, and the count in the sidebar
  // climbs with nothing on screen accounting for it.
  //
  // This is the way back to them: every outstanding entry, whatever its
  // date or class, oldest first, with the decision attached.
  const [outstandingOpen, setOutstandingOpen] = useState(false);
  const [outstandingCount, setOutstandingCount] = useState(0);

  const refreshOutstandingCount = useCallback(async () => {
    try {
      const r = await API.get('/approvals/pending-counts');
      setOutstandingCount(Number(r.data?.collector_approvals || 0));
    } catch { /* the badge is a convenience — never break the page for it */ }
  }, []);
  useEffect(() => { refreshOutstandingCount(); }, [refreshOutstandingCount]);

  // Today, so a day still to come is never counted as a day missed.
  const todayIso = useMemo(() => isoOf(new Date()), []);

  // ── Correcting a posted day ─────────────────────────────────────
  //
  // Held behind its own right, `collection.correct`. The probe ASKS THE
  // SERVER rather than reading the token, so the answer is the same one
  // the endpoint will give — a screen offering an action the API then
  // refuses is worse than one that never offered it.
  const [canCorrect, setCanCorrect] = useState(false);
  const [correcting, setCorrecting] = useState(null);
  // A term paid up front, taken from the learner's own row.
  const [prepayFor, setPrepayFor] = useState(null);

  useEffect(() => {
    let alive = true;
    API.get('/collections/corrections/window')
      .then(() => { if (alive) setCanCorrect(true); })
      .catch(() => { if (alive) setCanCorrect(false); });
    return () => { alive = false; };
  }, []);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const r = await API.get('/collections/week-sheet', {
        params: {
          from: range.from, to: range.to,
          ...(className ? { class_name: className } : {}),
          ...(incomeType ? { income_type: incomeType } : {}),
        },
      });
      setSheet(r.data || null);
      setPicked([]); setPage(1);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load the report');
      setSheet(null);
    } finally { setLoading(false); }
  }, [range, className, incomeType]);

  useEffect(() => { load(); }, [load]);

  const classes   = useMemo(() => sheet?.classes || [], [sheet]);
  const allTypes  = useMemo(() => sheet?.available_types || [], [sheet]);
  const types     = useMemo(() => sheet?.income_types || [], [sheet]);
  const students  = useMemo(() => sheet?.students || [], [sheet]);
  const dayList   = useMemo(() => sheet?.days || [], [sheet]);

  useEffect(() => { if (!className && classes.length) setClassName(classes[0]); }, [classes, className]);
  // Land on a single collection, so the report reads as one sheet
  // rather than a stack of them. "All collections" is still a choice.
  useEffect(() => {
    if (!incomeType && allTypes.length === 1) setIncomeType(allTypes[0]);
  }, [allTypes, incomeType]);

  const single = !!incomeType;
  const reportTitle = single
    ? `${prettyType(incomeType)} Collection Report`
    : 'Collection Report';

  // ── Keep the range to a week ────────────────────────────────────
  const generate = () => {
    let from = draftFrom, to = draftTo;
    if (!from || !to) { swal.warning('Pick both dates first.'); return; }
    if (to < from) { const t = from; from = to; to = t; }
    if (daysBetween(from, to) > MAX_DAYS - 1) {
      to = isoOf(addDays(new Date(`${from}T00:00:00`), MAX_DAYS - 1));
      swal.info(`A report covers at most ${MAX_DAYS} days — the end date was moved to ${longDate(to)}.`);
    }
    setDraftFrom(from); setDraftTo(to);
    setRange({ from, to });
  };

  const stepWeek = (n) => {
    const from = isoOf(addDays(new Date(`${range.from}T00:00:00`), 7 * n));
    const span = daysBetween(range.from, range.to);
    const to = isoOf(addDays(new Date(`${from}T00:00:00`), span));
    setDraftFrom(from); setDraftTo(to); setRange({ from, to });
  };

  const thisWeek = () => {
    const from = isoOf(mondayOf(new Date()));
    const to = isoOf(addDays(mondayOf(new Date()), 4));
    setDraftFrom(from); setDraftTo(to); setRange({ from, to });
  };

  // ── Per learner, for the whole range ────────────────────────────
  //
  // ONLY days that were actually marked count for money. This is the
  // whole point of the screen: it must say back exactly what the
  // teacher recorded, and nothing else.
  //
  // A day with no mark is not a debt. It may not have happened yet
  // (Tuesday, when today is Monday), the school may not have opened it,
  // or the teacher may not have walked it yet. Charging for those made
  // every learner in a part-marked week read "On Credit" while every
  // cell on screen said PAID — the report contradicting its own grid.
  const rowsOf = useCallback((s) => {
    const mine = types.filter(t => s.types?.[t]);
    let collected = 0, prepaid = 0, onCredit = 0, notEatenValue = 0, expected = 0;
    let creditDays = 0, notEatenDays = 0, paidDays = 0, marked = 0, unmarked = 0;

    // Money already on account, laid forward over the days still to
    // come — and what is left after this week.
    const advance = {};          // `${type}|${day}` → the rate it covers
    let heldNow = 0, heldLeft = 0, daysAhead = 0;

    // The running balance from subscription_balance. This is the figure
    // that MATTERS week to week: a learner who takes the meal on credit
    // every day owes more each week, and that total lives on the ledger,
    // not in whatever range happens to be on screen.
    let owing = 0;

    // What has been taken off this learner's balance on purpose. Sits
    // beside `owing` because it answers the other half of the same
    // question: a dash in the Owing column means either "never owed
    // anything" or "owed it and we cancelled it", and those are not the
    // same fact.
    let writtenOff = 0, writtenOffArrears = 0, writtenOffCredit = 0;

    for (const t of mine) {
      const fee   = Number(s.types[t].daily_amount || 0);
      owing   += Number(s.types[t].arrears || 0);
      writtenOff        += Number(s.types[t].written_off || 0);
      writtenOffArrears += Number(s.types[t].written_off_arrears || 0);
      writtenOffCredit  += Number(s.types[t].written_off_credit  || 0);
      let credit = Number(s.types[t].credit || 0);
      heldNow += credit;

      for (const d of dayList) {
        const c = s.cells?.[`${t}|${d}`];

        if (!c) {
          // A day still to come is not an omission. Only a day that has
          // already passed and was left blank is worth reporting.
          if (d < todayIso) { unmarked += 1; continue; }
          // Nothing marked yet — but if there is money on account, this
          // day is already paid for and should say so rather than sit
          // blank. Laid out day by day, in order, exactly as it will be
          // spent.
          if (fee > 0 && credit >= fee - 0.009) {
            credit -= fee;
            advance[`${t}|${d}`] = fee;
            daysAhead += 1;
          }
          continue;
        }

        marked += 1;
        const rate = Number(c.daily_amount || fee) || 0;
        expected += rate;
        if (c.status === 'absent') { notEatenDays += 1; notEatenValue += rate; continue; }
        const got  = Number(c.amount_paid || 0);
        const used = Number(c.credit_used || 0);
        collected += got;
        prepaid   += used;
        const short = rate - got - used;
        if (short > 0.009) { onCredit += short; creditDays += 1; }
        else paidDays += 1;
      }
      // Whatever the week did not use runs on into next week, and the
      // week after — a learner can pay a term up front.
      heldLeft += credit;
    }

    // One learner falls in exactly one bucket, so the counts add up to
    // the class — which is what the summary underneath claims.
    const bucket =
      marked === 0 && daysAhead === 0                       ? 'none'
      : marked === 0                                        ? 'paid'
      : (paidDays === 0 && creditDays === 0)                ? 'noteaten'
      : onCredit > 0.009                                    ? 'credit'
      : 'paid';

    const rate = mine.length ? Number(s.types[mine[0]].daily_amount || 0) : 0;
    // How much further the leftover advance reaches, in school days.
    const moreDays = rate > 0 ? Math.floor((heldLeft + 0.009) / rate) : 0;

    return {
      mine, collected, prepaid, onCredit, notEatenValue, expected,
      creditDays, notEatenDays, paidDays, marked, unmarked, bucket,
      advance, heldNow, heldLeft, daysAhead, moreDays, owing, rate,
      writtenOff, writtenOffArrears, writtenOffCredit,
    };
  }, [types, dayList, todayIso]);

  const rows = useMemo(
    () => students.map(s => ({ s, f: rowsOf(s) })), [students, rowsOf]);

  // ── The headline figures ────────────────────────────────────────
  // Every learner lands in exactly one of four buckets, so the cards
  // and the donut add up to the class instead of double-counting.
  const stats = useMemo(() => {
    let paidStudents = 0, creditStudents = 0, notEatenStudents = 0, unmarkedStudents = 0;
    let collected = 0, prepaid = 0, onCredit = 0, notEatenValue = 0, totalValue = 0;
    let markedDays = 0;
    // These two are LEDGER figures — they carry across every week, and
    // are the reason the sheet can answer "how much is this class
    // actually behind?" rather than only "what happened Monday?".
    let owingTotal = 0, owingStudents = 0, heldTotal = 0, heldStudents = 0;
    // Same footing as `owing` — the ledger, not the week — so the card
    // is exactly the sum of the Written off column beneath it.
    let writtenOffTotal = 0, writtenOffStudents = 0;
    for (const { f } of rows) {
      writtenOffTotal += f.writtenOff;
      if (f.writtenOff > 0.009) writtenOffStudents += 1;
      collected += f.collected; prepaid += f.prepaid; onCredit += f.onCredit;
      notEatenValue += f.notEatenValue; totalValue += f.expected;
      markedDays += f.marked;
      if (f.bucket === 'paid')          paidStudents += 1;
      else if (f.bucket === 'credit')   creditStudents += 1;
      else if (f.bucket === 'noteaten') notEatenStudents += 1;
      else                              unmarkedStudents += 1;
      owingTotal += f.owing;
      heldTotal  += f.heldNow;
      if (f.owing > 0.009)   owingStudents += 1;
      if (f.heldNow > 0.009) heldStudents += 1;
    }
    return {
      students: rows.length,
      paidStudents, creditStudents, notEatenStudents, unmarkedStudents,
      collected, prepaid, onCredit, notEatenValue, totalValue, markedDays,
      owingTotal, owingStudents, heldTotal, heldStudents,
      writtenOffTotal, writtenOffStudents,
    };
  }, [rows]);

  // ── What is waiting on approval ─────────────────────────────────
  const pendingCells = useMemo(() => {
    const out = [];
    for (const s of students) {
      for (const [key, c] of Object.entries(s.cells || {})) {
        if (String(c.approval_status || '').toUpperCase() !== 'PENDING') continue;
        if (!c.collection_id) continue;
        const [t, day] = key.split('|');
        if (incomeType && t !== incomeType) continue;
        out.push({ ...c, student_name: s.student_name, income_type: t, day });
      }
    }
    return out;
  }, [students, incomeType]);

  const pendingByDay = useMemo(() => {
    const m = {};
    for (const c of pendingCells) (m[c.day] = m[c.day] || []).push(c);
    return m;
  }, [pendingCells]);

  // Money the teacher has taken but not handed in. It is NOT approvable
  // — the round is still theirs until they submit it — but an approver
  // who sees "nothing waiting" over a sheet full of PAID cells needs to
  // be told why, or they will assume the screen is broken.
  const draftCells = useMemo(() => {
    const out = [];
    for (const s of students) {
      for (const [key, c] of Object.entries(s.cells || {})) {
        if (String(c.approval_status || '').toUpperCase() !== 'DRAFT') continue;
        const [t] = key.split('|');
        if (incomeType && t !== incomeType) continue;
        out.push({ ...c, collector_name: c.collector_name || '' });
      }
    }
    return out;
  }, [students, incomeType]);

  const draftValue = useMemo(
    () => draftCells.reduce((a, c) => a + Number(c.amount_paid || 0), 0), [draftCells]);
  const draftCollectors = useMemo(
    () => [...new Set(draftCells.map(c => c.collector_name).filter(Boolean))], [draftCells]);

  const pickedSet = useMemo(() => new Set(picked), [picked]);
  const togglePick = (id) => setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const approveIds = async (ids, what) => {
    if (!ids.length || working) return;
    const total = pendingCells.filter(c => ids.includes(c.collection_id))
      .reduce((a, c) => a + Number(c.amount_paid || 0), 0);
    const ok = await swal.confirm({
      title: `Approve ${what}?`,
      tone: 'success',
      html: `<b>${ids.length}</b> entr${ids.length === 1 ? 'y' : 'ies'} `
          + `&middot; <b>GH¢ ${money(total)}</b><br/><br/>`
          + 'This posts the money and issues the receipts. '
          + 'An approved entry can only be undone by a reversal.',
      confirmText: 'Yes, approve',
    });
    if (!ok) return;
    setWorking(true);
    try {
      const r = await API.post('/collections/bulk-approve', { collection_ids: ids });
      swal.success(r.data?.message || `Approved ${ids.length}`);
      await load();
    } catch (e) { swal.error(swal.pickError(e, 'Could not approve')); }
    finally { setWorking(false); }
  };

  // Rejecting lived only on the old flat list. That list is gone, so it
  // lives here — an approver who can only ever say yes is not an
  // approver. One reason covers the selection; the backend wants at
  // least five characters and rejects one at a time.
  const rejectPicked = async () => {
    if (!picked.length || working) return;
    const reason = await swal.prompt({
      title: `Reject ${picked.length} entr${picked.length === 1 ? 'y' : 'ies'}?`,
      text: 'Say why. The collector sees this, and it stays on the record.',
      inputType: 'textarea',
      inputPlaceholder: 'e.g. money not handed over at the office',
      confirmText: 'Reject them',
      // The backend refuses anything under five characters; catching it
      // here means the approver is told before the first request, not
      // after the last one.
      validate: (v) => (String(v || '').trim().length < 5
        ? 'Give a reason of at least 5 characters' : undefined),
    });
    if (!reason) return;
    setWorking(true);
    let done = 0; const failed = [];
    try {
      for (const id of picked) {
        try {
          await API.post(`/collections/${id}/reject`, { reason: String(reason).trim() });
          done += 1;
        } catch (e) { failed.push(swal.pickError(e, `#${id}`)); }
      }
      if (done) swal.success(`Rejected ${done}`);
      if (failed.length) swal.error(`${failed.length} could not be rejected — ${failed[0]}`);
      await load();
    } finally { setWorking(false); }
  };

  // ── Paging ──────────────────────────────────────────────────────
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const shown = useMemo(
    () => rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE), [rows, page]);
  useEffect(() => { if (page > pageCount) setPage(1); }, [page, pageCount]);

  // ── Print ───────────────────────────────────────────────────────
  const printReport = () => {
    const head = dayList.map(d => {
      const { dow, dm } = dayLabel(d);
      return `<th>${dow}<br><span class="sm">${dm}</span></th>`;
    }).join('');

    const body = rows.map(({ s, f }, i) => f.mine.map((t, ti) => {
      const cells = dayList.map(d => {
        const c = s.cells?.[`${t}|${d}`];
        const ahead = f.advance[`${t}|${d}`];
        if (ahead) return `<td class="c"><b>ADVANCE</b><br>${money(ahead)}</td>`;
        const w = c ? cellWord(c) : '—';
        const a = c ? money(cellAmount(c)) : '';
        return `<td class="c"><b>${w}</b><br>${a}</td>`;
      }).join('');
      const span = f.mine.length;
      return `<tr>
        ${ti === 0 ? `<td rowspan="${span}">${i + 1}</td>
                      <td rowspan="${span}" class="nm">${s.student_name}</td>` : ''}
        ${single ? '' : `<td>${prettyType(t)}</td>`}
        <td class="r">${money(s.types[t].daily_amount)}</td>
        ${cells}
        ${ti === 0 ? `<td rowspan="${span}" class="r"><b>${money(f.collected)}</b></td>
                      <td rowspan="${span}" class="r">${f.owing > 0.009 ? money(f.owing) : '-'}</td>
                      <td rowspan="${span}">${STATUS_LABEL[f.bucket].word}</td>` : ''}
      </tr>`;
    }).join('')).join('');

    const html = `<!doctype html><html><head><meta charset="utf-8">
      <title>${reportTitle} — ${className}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: Arial, Helvetica, sans-serif; color:#111; }
        h1 { font-size: 15pt; margin:0 0 2px; }
        .sub { font-size: 9pt; color:#555; margin-bottom: 8px; }
        table { width:100%; border-collapse: collapse; }
        th, td { border:1px solid #999; padding:3px 4px; font-size:8pt; text-align:center; }
        th { background:#eaf2fb; }
        .sm { font-weight: normal; font-size: 7pt; color:#555; }
        .nm { text-align:left; font-weight:bold; }
        .r { text-align:right; }
        .c { font-size: 7.5pt; }
        .tot { margin-top:10px; font-size:9pt; }
        .tot b { display:inline-block; min-width:170px; }
      </style></head><body>
      <h1>${reportTitle} — ${className}</h1>
      <div class="sub">${rangeLabel(range.from, range.to)} &middot; printed ${new Date().toLocaleString()}</div>
      <table>
        <thead><tr>
          <th>#</th><th>Student Name</th>
          ${single ? '' : '<th>Collection</th>'}
          <th>Rate<br><span class="sm">(GH¢)</span></th>
          ${head}
          <th>Total<br><span class="sm">(GH¢)</span></th>
          <th>Owing<br><span class="sm">(GH¢)</span></th><th>Status</th>
        </tr></thead>
        <tbody>${body}</tbody>
      </table>
      <div class="tot">
        <div><b>Total students</b> ${stats.students}</div>
        <div><b>Total value</b> GH¢ ${money(stats.totalValue)}</div>
        <div><b>Amount collected</b> GH¢ ${money(stats.collected)}</div>
        <div><b>Amount on credit</b> GH¢ ${money(stats.onCredit)}</div>
        <div><b>Not eaten value</b> GH¢ ${money(stats.notEatenValue)}</div>
        ${stats.writtenOffTotal > 0.009
          ? `<div><b>Written off</b> GH¢ ${money(stats.writtenOffTotal)}`
            + ` (${stats.writtenOffStudents} learner${stats.writtenOffStudents === 1 ? '' : 's'})</div>`
          : ''}
      </div></body></html>`;

    const w = window.open('', '_blank');
    if (!w) { swal.warning('Allow pop-ups to print this report.'); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 300);
  };

  // ── CSV ─────────────────────────────────────────────────────────
  // For anyone who wants the figures somewhere other than Excel. A
  // BOM so Excel on Windows still reads the ¢ correctly.
  const exportCsv = () => {
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const head = ['#', 'Student Name', 'Index',
      ...(single ? [] : ['Collection']), 'Rate',
      ...dayList.map(d => { const { dow, dm } = dayLabel(d); return `${dow} ${dm}`; }),
      'Total', 'Owing', 'Advance held', 'Status'];
    const lines = [head.map(esc).join(',')];
    for (const [i, { s, f }] of rows.entries()) {
      for (const [ti, t] of f.mine.entries()) {
        const cells = dayList.map(d => {
          const ahead = f.advance[`${t}|${d}`];
          if (ahead) return `ADVANCE ${money(ahead)}`;
          const c = s.cells?.[`${t}|${d}`];
          if (!c) return '—';
          return `${cellWord(c)} ${money(cellAmount(c))}`;
        });
        lines.push([
          ti === 0 ? i + 1 : '',
          ti === 0 ? s.student_name : '',
          ti === 0 ? s.student_id : '',
          ...(single ? [] : [prettyType(t)]),
          Number(s.types[t].daily_amount || 0).toFixed(2),
          ...cells,
          ti === 0 ? f.collected.toFixed(2) : '',
          ti === 0 ? f.owing.toFixed(2) : '',
          ti === 0 ? f.heldNow.toFixed(2) : '',
          ti === 0 ? STATUS_LABEL[f.bucket].word : '',
        ].map(esc).join(','));
      }
    }
    lines.push('');
    lines.push([esc('Total students'), esc(stats.students)].join(','));
    lines.push([esc('Total value'), esc(stats.totalValue.toFixed(2))].join(','));
    lines.push([esc('Amount collected'), esc(stats.collected.toFixed(2))].join(','));
    lines.push([esc('Amount on credit'), esc(stats.onCredit.toFixed(2))].join(','));
    lines.push([esc('Not eaten amount'), esc(stats.notEatenValue.toFixed(2))].join(','));

    const blob = new Blob(['﻿', lines.join('\r\n')],
      { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${reportTitle}-${className}-${range.from}`
      .replace(/[^A-Za-z0-9_-]+/g, '_') + '.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // ── Excel ───────────────────────────────────────────────────────
  const exportExcel = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      wb.created = new Date();
      const ws = wb.addWorksheet(String(className || 'Report').slice(0, 28), {
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      });

      ws.addRow([`${reportTitle} — ${className}`]);
      ws.addRow([rangeLabel(range.from, range.to)]);
      ws.addRow([]);
      ws.getRow(1).font = { bold: true, size: 14 };
      ws.getRow(2).font = { italic: true, size: 10 };

      const header = ['#', 'Student Name',
        ...(single ? [] : ['Collection']), 'Rate (GH¢)',
        ...dayList.map(d => { const { dow, dm } = dayLabel(d); return `${dow} ${dm}`; }),
        'Total (GH¢)', 'Owing (GH¢)', 'Advance (GH¢)', 'Status'];
      const hr = ws.addRow(header);
      hr.font = { bold: true };
      hr.eachCell(c => {
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAF2FB' } };
        c.alignment = { horizontal: 'center', wrapText: true };
        c.border = { top: { style: 'thin' }, left: { style: 'thin' },
                     bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      for (const [i, { s, f }] of rows.entries()) {
        for (const [ti, t] of f.mine.entries()) {
          const cells = dayList.map(d => {
            const ahead = f.advance[`${t}|${d}`];
            if (ahead) return `ADVANCE ${money(ahead)}`;
            const c = s.cells?.[`${t}|${d}`];
            if (!c) return '—';
            return `${cellWord(c)} ${money(cellAmount(c))}`;
          });
          const r = ws.addRow([
            ti === 0 ? i + 1 : '',
            ti === 0 ? s.student_name : '',
            ...(single ? [] : [prettyType(t)]),
            Number(s.types[t].daily_amount || 0),
            ...cells,
            ti === 0 ? Number(f.collected.toFixed(2)) : '',
            ti === 0 ? Number(f.owing.toFixed(2)) : '',
            ti === 0 ? Number(f.heldNow.toFixed(2)) : '',
            ti === 0 ? STATUS_LABEL[f.bucket].word : '',
          ]);
          r.eachCell(c => {
            c.border = { top: { style: 'hair' }, left: { style: 'hair' },
                         bottom: { style: 'hair' }, right: { style: 'hair' } };
          });
        }
      }

      ws.addRow([]);
      ws.addRow(['Total students', stats.students]).font = { bold: true };
      ws.addRow(['Total value (GH¢)', Number(stats.totalValue.toFixed(2))]);
      ws.addRow(['Amount collected (GH¢)', Number(stats.collected.toFixed(2))]);
      ws.addRow(['Amount on credit (GH¢)', Number(stats.onCredit.toFixed(2))]);
      ws.addRow(['Not eaten value (GH¢)', Number(stats.notEatenValue.toFixed(2))]);

      ws.columns.forEach((col, i) => { col.width = i === 1 ? 28 : 13; });
      ws.views = [{ state: 'frozen', ySplit: 4 }];

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${reportTitle}-${className}-${range.from}`
        .replace(/[^A-Za-z0-9_-]+/g, '_') + '.xlsx';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      swal.error(e?.message || 'Excel export failed');
    }
  };

  // ── Render ──────────────────────────────────────────────────────
  const dayColCount = dayList.length;

  return (
    <Box sx={{ maxWidth: 1560, mx: 'auto', p: { xs: 1, sm: 2 } }}>

      {/* ══ Header: what report, which class, which dates ═════════ */}
      <Card elevation={0} sx={{
        p: { xs: 1.4, md: 1.9 }, mb: 1.4, borderRadius: 3,
        bgcolor: '#fff', border: '1px solid #e6ecf4',
      }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.6}
          alignItems={{ lg: 'center' }} justifyContent="space-between">

          <Stack direction="row" spacing={1.6} alignItems="center">
            <Box sx={{
              width: 50, height: 50, borderRadius: 2.5, flexShrink: 0,
              bgcolor: '#2f6fb0', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <ReportIcon sx={{ fontSize: 27 }} />
            </Box>
            <Box>
              <Typography sx={{
                fontSize: { xs: 17, md: 22 }, fontWeight: 800,
                color: '#1e3a6b', lineHeight: 1.2,
              }}>
                {reportTitle}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.5 }}>
                What was collected, given on credit and left outstanding
                for the class and dates chosen.
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {allTypes.length > 1 && (
              <TextField select size="small" label="Collection" value={incomeType}
                onChange={(e) => setIncomeType(e.target.value)} sx={{ minWidth: 152 }}>
                <MenuItem value="">All collections</MenuItem>
                {allTypes.map(t => <MenuItem key={t} value={t}>{prettyType(t)}</MenuItem>)}
              </TextField>
            )}
            <TextField select size="small" label="Class" value={className}
              onChange={(e) => setClassName(e.target.value)} sx={{ minWidth: 148 }}>
              {classes.length === 0 && <MenuItem value="">—</MenuItem>}
              {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField size="small" type="date" label="From Date" value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 158 }} />
            <TextField size="small" type="date" label="To Date" value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 158 }} />
            <Button variant="contained" disableElevation onClick={generate}
              startIcon={<GenerateIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: 13.5,
                px: 2.2, py: 1.1, borderRadius: 2, whiteSpace: 'nowrap',
                bgcolor: '#2f6fb0', '&:hover': { bgcolor: '#1e3a6b' },
              }}>
              Generate Report
            </Button>
          </Stack>
        </Stack>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 1.4 }}>{error}</Alert>}

      {/* ══ The five headline figures ═════════════════════════════ */}
      <Stack direction="row" spacing={1.2} sx={{ mb: 1.4 }} flexWrap="wrap" useFlexGap>
        <StatCard icon={<PaidIcon />} label="Paid Students"
          value={stats.paidStudents} sub={`(GH¢ ${money(stats.collected)})`}
          tone={{ bg: '#f1faf5', line: '#cfe7d6', fg: '#15803d', chip: '#22a559', sub: '#4a9b6b' }} />
        <StatCard icon={<CreditIcon />} label="Credit Students"
          value={stats.creditStudents} sub={`(GH¢ ${money(stats.onCredit)})`}
          tone={{ bg: '#fdf8ed', line: '#f3e0ba', fg: '#b8860b', chip: '#efa22e', sub: '#b8913f' }} />
        <StatCard icon={<NotEatenIcon />} label="Not Eaten"
          value={stats.notEatenStudents} sub={`(GH¢ ${money(stats.notEatenValue)})`}
          tone={{ bg: '#fdf3f3', line: '#f6cccc', fg: '#c0392b', chip: '#e85d5d', sub: '#c07a72' }} />
        <StatCard icon={<PeopleIcon />} label="Total Students"
          value={stats.students} sub={`(GH¢ ${money(stats.totalValue)})`}
          tone={{ bg: '#f2f7fd', line: '#dce7f5', fg: '#1c5b96', chip: '#4a90d9', sub: '#6d8dae' }} />
        <StatCard icon={<ValueIcon />} label="Total Value"
          value={`GH¢ ${money(stats.totalValue)}`}
          tone={{ bg: '#f7f3fd', line: '#e2d8f5', fg: '#6b3fa0', chip: '#8b5cd6', sub: '#8a76a8' }} />
        {/* Only once there is something to report. A "Written off: 0"
            card on every sheet in the school would be five-sixths noise,
            and it is the one figure here that should stand out when it
            is not zero. Exactly the sum of the Written off amounts on
            the rows below. */}
        {stats.writtenOffTotal > 0.009 && (
          <StatCard icon={<WriteOffIcon />} label="Written Off"
            value={`GH¢ ${money(stats.writtenOffTotal)}`}
            sub={`(${stats.writtenOffStudents} learner${stats.writtenOffStudents === 1 ? '' : 's'})`}
            tone={{ bg: '#fffbf3', line: '#fcd9a6', fg: '#92400e', chip: '#d97706', sub: '#a97c50' }} />
        )}
      </Stack>

      {/* ══ The report table ══════════════════════════════════════ */}
      <Card elevation={0} sx={{
        borderRadius: 3, overflow: 'hidden', mb: 1.4,
        bgcolor: '#fff', border: '1px solid #e3eaf3',
      }}>
        {/* Title on the left, tools on the right — and the approval
            buttons live HERE rather than in a banner of their own. A
            separate amber strip above the table was a second headline
            competing with the five real ones. */}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}
          alignItems={{ md: 'center' }} justifyContent="space-between"
          sx={{ px: 2, py: 1.5, borderBottom: '1px solid #e8eef6' }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#1e3a6b' }}>
              {single ? prettyType(incomeType) : 'Collection'} Details
              <Box component="span" sx={{ fontWeight: 700, color: '#64748b' }}>
                {className ? ` (${className})` : ''}
              </Box>
            </Typography>
            {/* Nothing here can be approved until the teacher hands it
                in. Saying so is the difference between "the screen is
                broken" and "go and ask them to submit". */}
            {draftCells.length > 0 && (
              <Tooltip title={draftCollectors.length
                ? `Still with ${draftCollectors.join(', ')}`
                : 'Still with the collector'}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748b', mt: 0.2 }}>
                  <Box component="span" sx={{
                    display: 'inline-block', width: 9, height: 9, mr: 0.7,
                    borderRadius: 0.5, border: '1px dashed #94a3b8',
                    verticalAlign: 'middle',
                  }} />
                  {draftCells.length} entr{draftCells.length === 1 ? 'y' : 'ies'}
                  {' '}· GH¢ {money(draftValue)} not yet submitted — cannot be approved
                </Typography>
              </Tooltip>
            )}
          </Box>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {/* Stepping a week at a time sits beside the date it moves,
                not in a row of its own under the header. */}
            <Stack direction="row" spacing={0.2} alignItems="center">
              <Tooltip title="Previous week">
                <IconButton size="small" onClick={() => stepWeek(-1)}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
              </Tooltip>
              <Typography sx={{ fontSize: 11.5, color: '#64748b', whiteSpace: 'nowrap' }}>
                <Box component="b" sx={{ color: '#334155' }}>Date:</Box>{' '}
                {rangeLabel(range.from, range.to)}
              </Typography>
              <Tooltip title="Next week">
                <IconButton size="small" onClick={() => stepWeek(1)}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="This week (Mon–Fri)">
                <IconButton size="small" onClick={thisWeek}>
                  <TodayIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Beside Print and Export because it belongs to the same
                "what do I do with this sheet" group, and because the
                history has to be reachable without first finding a
                learner who happens to have a balance. */}
            {/* Deliberately loud, and deliberately first. An entry that
                was never approved is money in limbo: the collector has
                handed it in and the books have not taken it. It should
                not be findable only by guessing which week it was. */}
            {canApprove && outstandingCount > 0 && (
              <Button size="small" variant="contained" disableElevation
                startIcon={<PendingIcon />}
                onClick={() => setOutstandingOpen(true)}
                sx={{
                  textTransform: 'none', fontWeight: 800, borderRadius: 1.5, px: 1.6,
                  bgcolor: '#b45309', '&:hover': { bgcolor: '#92400e' },
                }}>
                {outstandingCount} awaiting approval
              </Button>
            )}

            {canApprove && (
              <Button size="small" variant="outlined" startIcon={<WriteOffIcon />}
                onClick={() => setHistoryOpen(true)}
                sx={{
                  textTransform: 'none', fontWeight: 700, borderRadius: 1.5,
                  color: '#92400e', borderColor: '#fcd9a6', bgcolor: '#fffbf3',
                  px: 1.6, '&:hover': { borderColor: '#d97706', bgcolor: '#fef3e2' },
                }}>
                Write-offs
              </Button>
            )}

            <Button size="small" variant="outlined" startIcon={<PrintIcon />}
              onClick={printReport}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: 1.5,
                color: '#334155', borderColor: '#d8e1ec', bgcolor: '#fff',
                px: 1.6, '&:hover': { borderColor: '#94a3b8', bgcolor: '#f8fafc' },
              }}>
              Print
            </Button>

            <Button size="small" variant="outlined" startIcon={<ExcelIcon />}
              endIcon={<CaretIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => setExportAnchor(e.currentTarget)}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: 1.5,
                color: '#15803d', borderColor: '#bfe5cf', bgcolor: '#f2faf5',
                px: 1.6, '&:hover': { borderColor: '#2e9e4f', bgcolor: '#e8f6ed' },
              }}>
              Export
            </Button>
            <Menu anchorEl={exportAnchor} open={!!exportAnchor}
              onClose={() => setExportAnchor(null)}>
              <MenuItem onClick={() => { setExportAnchor(null); exportExcel(); }}>
                Excel workbook (.xlsx)
              </MenuItem>
              <MenuItem onClick={() => { setExportAnchor(null); exportCsv(); }}>
                Comma-separated (.csv)
              </MenuItem>
              <MenuItem onClick={() => { setExportAnchor(null); printReport(); }}>
                Print / save as PDF
              </MenuItem>
            </Menu>

            {picked.length > 0 ? (
              <>
                <Button size="small" variant="outlined" disabled={working}
                  startIcon={<RejectIcon />} onClick={rejectPicked}
                  sx={{
                    textTransform: 'none', fontWeight: 700, borderRadius: 1.5, px: 1.6,
                    color: '#b91c1c', borderColor: '#f3c9c9', bgcolor: '#fff',
                    '&:hover': { borderColor: '#e13b3b', bgcolor: '#fef3f3' },
                  }}>
                  Reject ({picked.length})
                </Button>
                <Button size="small" variant="contained" disableElevation disabled={working}
                  startIcon={<ApproveIcon />}
                  onClick={() => approveIds(picked, `${picked.length} selected`)}
                  sx={{
                    textTransform: 'none', fontWeight: 700, borderRadius: 1.5, px: 1.6,
                    bgcolor: '#1e3a6b', '&:hover': { bgcolor: '#16294d' },
                  }}>
                  Approve ({picked.length})
                </Button>
              </>
            ) : pendingCells.length > 0 && (
              <Button size="small" variant="contained" disableElevation disabled={working}
                startIcon={working
                  ? <CircularProgress size={14} color="inherit" />
                  : <ApproveIcon />}
                onClick={() => approveIds(
                  pendingCells.map(c => c.collection_id), 'everything in this range')}
                sx={{
                  textTransform: 'none', fontWeight: 700, borderRadius: 1.5, px: 1.6,
                  bgcolor: '#22a559', '&:hover': { bgcolor: '#15803d' },
                }}>
                Approve all ({pendingCells.length})
              </Button>
            )}
          </Stack>
        </Stack>

        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
        ) : (
          <TableContainer sx={{ maxHeight: { xs: 'none', md: '72vh' } }}>
            <Table size="small" stickyHeader sx={{
              borderCollapse: 'separate', borderSpacing: 0,
              /* Tight. Two collections per learner means a class of 25
                 is 50 rows; every pixel of padding costs a learner off
                 the screen, and an approver wants the class in one
                 view, not in three scrolls. */
              '& th': {
                fontWeight: 800, fontSize: 10.5, lineHeight: 1.25,
                py: 0.7, px: 1,
                borderRight: '1px solid rgba(255,255,255,.18)',
                position: { xs: 'static', md: 'sticky' },
              },
              '& th:last-of-type': { borderRight: 0 },
              '& td': {
                fontSize: 11.5, color: '#1e293b', py: 0.28, px: 1,
                borderBottom: '1px solid #eef2f7',
                borderRight: '1px solid #eef2f7',
              },
              '& td:last-of-type': { borderRight: 0 },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 34 }}>#</TableCell>
                  <TableCell sx={{ minWidth: 168 }}>Student Name</TableCell>
                  {!single && <TableCell sx={{ width: 96 }}>Collection</TableCell>}
                  <TableCell align="center" sx={{ width: 74 }}>Rate (GH¢)</TableCell>
                  {dayList.map(d => {
                    const { dow, dm } = dayLabel(d);
                    const waiting = (pendingByDay[d] || []).length;
                    return (
                      <TableCell key={d} align="center" sx={{ minWidth: 74 }}>
                        {/* Day, date and the day's approve button on one
                            line each — the chip used to add a third row
                            to every header and push the grid down. */}
                        <Stack direction="row" spacing={0.6} alignItems="center"
                          justifyContent="center">
                          <Box component="span">{dow}</Box>
                          <Box component="span" sx={{ fontWeight: 600, fontSize: 9, opacity: .8 }}>
                            {dm}
                          </Box>
                        </Stack>
                        {/* One day at a time is how a school actually
                            does it — yesterday's round checked and
                            cleared this morning, from its own column. */}
                        {waiting > 0 && (
                          <Chip size="small" clickable label={`Approve ${waiting}`}
                            onClick={() => approveIds(
                              (pendingByDay[d] || []).map(c => c.collection_id), `${dow} ${dm}`)}
                            sx={{
                              mt: 0.3, height: 17, fontSize: 8.5, fontWeight: 800,
                              '& .MuiChip-label': { px: 0.7 },
                              bgcolor: '#fdf2e0', color: '#a1610a',
                              border: '1px solid #f3ddb8',
                              '&:hover': { bgcolor: '#f7e3c0' },
                            }} />
                        )}
                      </TableCell>
                    );
                  })}
                  <TableCell align="center" sx={{ width: 84 }}>Total (GH¢)</TableCell>
                  {/* The running balance, not this week's shortfall. It
                      is what the learner owes today, carried from every
                      week before this one. */}
                  <TableCell align="center" sx={{ width: 86 }}>Owing (GH¢)</TableCell>
                  <TableCell align="center" sx={{ width: 104 }}>Status</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {shown.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6 + dayColCount} align="center"
                      sx={{ py: 7, color: 'text.secondary' }}>
                      {className
                        ? 'Nobody in this class is registered for a daily collection in these dates.'
                        : 'Choose a class to begin.'}
                    </TableCell>
                  </TableRow>
                )}

                {shown.map(({ s, f }, idx) => {
                  const i = (page - 1) * PAGE_SIZE + idx;
                  // White rows. The day cells already carry four
                  // colours; a banded ground behind them turned the
                  // sheet into a colour chart.
                  const band = '#ffffff';
                  const n = Math.max(1, f.mine.length);

                  return f.mine.map((t, ti) => (
                    <TableRow key={`${s.student_id}|${t}`} sx={{
                      bgcolor: band,
                      '&:hover td': { bgcolor: '#eef4fb' },
                    }}>
                      {ti === 0 && (
                        <>
                          <TableCell rowSpan={n} sx={{
                            color: '#94a3b8', fontWeight: 600, verticalAlign: 'middle',
                            borderBottom: '1px solid #e2e9f2', ...TNUM,
                          }}>
                            {i + 1}
                          </TableCell>
                          <TableCell rowSpan={n} sx={{
                            verticalAlign: 'middle',
                            borderBottom: '1px solid #e2e9f2',
                          }}>
                            {/* Black. Blue reads as a link, and there is
                                nothing here to follow. */}
                            <Tooltip title={s.student_id || ''}>
                              <Typography component="span" sx={{
                                fontSize: 12, fontWeight: 700, color: '#0f172a',
                              }}>
                                {s.student_name}
                              </Typography>
                            </Tooltip>
                            {/* Muted slate, not amber. "Unmarked" is a
                                fact about the week, not a warning about
                                the money — amber here competed with the
                                cells that genuinely want attention. */}
                            {f.unmarked > 0 && (
                              <Typography sx={{
                                fontSize: 9, fontWeight: 600, color: '#94a3b8', lineHeight: 1.4,
                              }}>
                                {f.unmarked} day{f.unmarked === 1 ? '' : 's'} unmarked
                              </Typography>
                            )}
                            {/* Money on account, and how far it reaches.
                                A learner can pay a term up front; the
                                weeks after this one keep filling in. */}
                            {f.heldNow > 0.009 && (
                              <Tooltip title={`GH¢ ${money(f.heldNow)} on account`
                                + ` · ${f.daysAhead} day${f.daysAhead === 1 ? '' : 's'} covered in this week`
                                + (f.moreDays > 0
                                    ? ` · about ${f.moreDays} more day${f.moreDays === 1 ? '' : 's'} after it`
                                    : '')}>
                                <Typography sx={{
                                  fontSize: 9, fontWeight: 700, color: '#4338ca', lineHeight: 1.4,
                                }}>
                                  Advance GH¢ {money(f.heldNow)}
                                  {f.moreDays > 0 ? ` · ${f.moreDays}d ahead` : ''}
                                </Typography>
                              </Tooltip>
                            )}
                            {/* "Let me pay the rest of the term" is said
                                while looking at this row, not while
                                standing at another screen. Taking it
                                here means the learner is never searched
                                for a second time — which is how a
                                payment lands on the wrong child. */}
                            {canApprove && (
                              <Tooltip title="Take a term, month or year up front — posts to the cash book, not to a teacher">
                                <Box component="button" type="button"
                                  onClick={() => setPrepayFor(s)}
                                  sx={{
                                    display: 'block', mt: 0.3,
                                    border: 'none', background: 'none', cursor: 'pointer',
                                    font: 'inherit', fontSize: 9, fontWeight: 800,
                                    color: '#15803d', textDecoration: 'underline',
                                    p: 0, '&:hover': { color: '#166534' },
                                  }}>
                                  Pay ahead
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                        </>
                      )}

                      {!single && (
                        <TableCell sx={{ borderBottom: ti === n - 1 ? '1px solid #e2e9f2' : undefined }}>
                          <Chip size="small" label={prettyType(t)}
                            sx={{
                              height: 18, fontSize: 9, fontWeight: 700,
                              '& .MuiChip-label': { px: 0.8 },
                              bgcolor: '#eef4fb', color: '#1c5b96', border: '1px solid #dce7f5',
                            }} />
                        </TableCell>
                      )}

                      <TableCell align="center" sx={{
                        ...TNUM, fontWeight: 700, color: '#0f172a',
                        borderBottom: ti === n - 1 ? '1px solid #e2e9f2' : undefined,
                      }}>
                        {money(s.types[t].daily_amount)}
                      </TableCell>

                      {dayList.map(d => {
                        const c = s.cells?.[`${t}|${d}`];
                        const canPick = !!c?.collection_id
                          && String(c.approval_status || '').toUpperCase() === 'PENDING';
                        const ahead = f.advance[`${t}|${d}`];
                        return (
                          <TableCell key={d} align="center" sx={{
                            p: 0.3,
                            borderBottom: ti === n - 1 ? '1px solid #e2e9f2' : undefined,
                          }}>
                            <Cell mark={c} advance={ahead}
                              advanceNote={f.moreDays > 0
                                ? `GH¢ ${money(f.heldLeft)} still on account after this week`
                                  + ` — about ${f.moreDays} more day${f.moreDays === 1 ? '' : 's'}`
                                : 'this is the last day the advance covers'}
                              selectable={canPick}
                              selected={canPick && pickedSet.has(c.collection_id)}
                              onToggle={() => togglePick(c.collection_id)}
                              /* A tap PICKS a pending cell for approval;
                                 a long press — or a right-click on a
                                 desk — opens the correction. Two very
                                 different actions must not share one
                                 gesture. */
                              onCorrect={canCorrect && d <= todayIso
                                ? () => setCorrecting({
                                    cell: c, studentId: s.student_id,
                                    studentName: s.student_name,
                                    className: s.class_name,
                                    incomeType: t, date: d,
                                    rate: s.types[t].daily_amount,
                                  })
                                : null} />
                          </TableCell>
                        );
                      })}

                      {ti === 0 && (
                        <>
                          <TableCell rowSpan={n} align="center" sx={{
                            verticalAlign: 'middle', ...TNUM,
                            fontSize: 14, fontWeight: 800, color: '#0f172a',
                            borderBottom: '1px solid #e2e9f2',
                          }}>
                            {money(f.collected)}
                          </TableCell>

                          {/* The ledger balance, which is what grows if
                              a learner keeps taking the meal on credit.
                              This week's shortfall is inside it. */}
                          <TableCell rowSpan={n} align="center" sx={{
                            verticalAlign: 'middle', ...TNUM,
                            fontSize: 13.5, fontWeight: 800,
                            color: f.owing > 0.009 ? '#b91c1c' : '#cbd5e1',
                            borderBottom: '1px solid #e2e9f2',
                          }}>
                            <Tooltip title={f.owing > 0.009
                              ? `Owed in total, carried across every week`
                                + (f.onCredit > 0.009
                                    ? ` · GH¢ ${money(f.onCredit)} of it was added in these dates`
                                    : ' · nothing added in these dates')
                              : f.heldNow > 0.009
                                ? `Nothing owed — GH¢ ${money(f.heldNow)} paid in advance`
                                : 'Nothing owed'}>
                              <Box component="span">
                                {f.owing > 0.009 ? money(f.owing) : '—'}
                              </Box>
                            </Tooltip>
                            {/* A dash in this column means one of two
                                very different things: never owed
                                anything, or owed it and we cancelled it.
                                Saying which is the whole reason the
                                write-off is worth recording. */}
                            {f.writtenOff > 0.009 && (
                              <Tooltip title={
                                `GH¢ ${money(f.writtenOff)} written off in total`
                                + (f.writtenOffArrears > 0.009
                                    ? ` · GH¢ ${money(f.writtenOffArrears)} of arrears` : '')
                                + (f.writtenOffCredit > 0.009
                                    ? ` · GH¢ ${money(f.writtenOffCredit)} of credit` : '')
                                + ' — open Write-offs for the reason'}>
                                <Typography sx={{
                                  fontSize: 9.5, fontWeight: 800, lineHeight: 1.4,
                                  color: '#b45309', whiteSpace: 'nowrap',
                                }}>
                                  Written off {money(f.writtenOff)}
                                </Typography>
                              </Tooltip>
                            )}
                            {/* Correcting a balance starts where the
                                balance is read. Shown only when there is
                                something to correct — arrears or credit —
                                so a square learner's row stays clean. */}
                            {canApprove && (f.owing > 0.009 || f.heldNow > 0.009) && (
                              <Tooltip title="Write off arrears or credit for this learner">
                                <Box component="button" type="button"
                                  onClick={() => setWriteOffFor(s)}
                                  sx={{
                                    display: 'block', mx: 'auto', mt: 0.3,
                                    border: 'none', background: 'none', cursor: 'pointer',
                                    font: 'inherit', fontSize: 9, fontWeight: 800,
                                    color: '#b45309', textDecoration: 'underline',
                                    p: 0, '&:hover': { color: '#92400e' },
                                  }}>
                                  Write off
                                </Box>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell rowSpan={n} align="center" sx={{
                            verticalAlign: 'middle',
                            borderBottom: '1px solid #e2e9f2',
                          }}>
                            {/* Four states, not two. "Fully Paid" on a
                                learner nobody has marked yet would be a
                                lie, and "On Credit" on one was the bug
                                that made every row read as owing. */}
                            <Tooltip title={
                              f.bucket === 'none'
                                ? 'Nothing has been marked for these dates yet'
                              : f.bucket === 'credit'
                                ? `GH¢ ${money(f.onCredit)} outstanding on `
                                  + `${f.creditDays} day${f.creditDays === 1 ? '' : 's'}`
                              : f.bucket === 'noteaten'
                                ? `Took nothing on ${f.notEatenDays} marked day`
                                  + `${f.notEatenDays === 1 ? '' : 's'} — nothing owed`
                                : `${f.paidDays} day${f.paidDays === 1 ? '' : 's'} paid in full`
                                  + (f.prepaid > 0.009 ? ` · GH¢ ${money(f.prepaid)} from advance` : '')}>
                              <Chip size="small"
                                label={STATUS_LABEL[f.bucket].word}
                                sx={{
                                  height: 23, minWidth: 88, borderRadius: 999,
                                  fontSize: 10.5, fontWeight: 700, border: 'none',
                                  bgcolor: STATUS_LABEL[f.bucket].bg,
                                  color:   STATUS_LABEL[f.bucket].fg,
                                }} />
                            </Tooltip>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ));
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {rows.length > 0 && (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}
            alignItems="center" justifyContent="space-between"
            sx={{ px: 2, py: 1.2, borderTop: '1px solid #e8eef6', bgcolor: '#fff' }}>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>
              Showing {(page - 1) * PAGE_SIZE + 1} – {Math.min(page * PAGE_SIZE, rows.length)}
              {' '}of {rows.length} students
            </Typography>
            <Pagination size="small" shape="rounded" count={pageCount} page={page}
              showFirstButton
              onChange={(_, p) => setPage(p)}
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 700, fontSize: 12, borderRadius: 1.5,
                  border: '1px solid #e2e9f2', color: '#475569',
                },
                '& .Mui-selected': {
                  bgcolor: '#2f6fb0 !important', color: '#fff',
                  borderColor: '#2f6fb0',
                },
              }} />
          </Stack>
        )}
      </Card>

      {/* ══ Summary, money, and adding somebody ═══════════════════ */}
      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.4} alignItems="stretch">

        <Card elevation={0} sx={{
          p: 2, borderRadius: 3, flex: '1 1 320px',
          bgcolor: '#fff', border: '1px solid #e3eaf3',
        }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#1e3a6b', mb: 1.4 }}>
            Collection Summary
          </Typography>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Donut total={stats.students} caption="Students"
              slices={[
                { label: 'Paid',      value: stats.paidStudents,     colour: '#22a559' },
                { label: 'Credit',    value: stats.creditStudents,   colour: '#efa22e' },
                { label: 'Not Eaten', value: stats.notEatenStudents, colour: '#e85d5d' },
                { label: 'Not marked', value: stats.unmarkedStudents, colour: '#cbd5e1' },
              ]} />
            <Stack spacing={1.6} sx={{ flex: 1 }}>
              {[
                ['Paid', stats.paidStudents, '#22a559'],
                ['Credit', stats.creditStudents, '#efa22e'],
                ['Not Eaten', stats.notEatenStudents, '#e85d5d'],
                ...(stats.unmarkedStudents ? [['Not marked', stats.unmarkedStudents, '#cbd5e1']] : []),
              ].map(([label, value, colour]) => (
                <Stack key={label} direction="row" spacing={1.2} alignItems="center">
                  <Box sx={{ width: 11, height: 11, borderRadius: '50%', bgcolor: colour, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 13, color: '#334155', flex: 1 }}>{label}</Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#0f172a', ...TNUM }}>
                    {value}
                  </Typography>
                  <Typography sx={{
                    fontSize: 12, color: '#94a3b8', minWidth: 46,
                    textAlign: 'right', ...TNUM,
                  }}>
                    ({stats.students ? Math.round((value / stats.students) * 100) : 0}%)
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Card>

        <Card elevation={0} sx={{
          p: 2, borderRadius: 3, flex: '1 1 330px',
          bgcolor: '#fff', border: '1px solid #e3eaf3',
        }}>
          <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#1e3a6b', mb: 1.4 }}>
            Financial Summary
          </Typography>
          <Stack spacing={1.25}>
            {/* Value counts the days that were actually MARKED, not
                every day in the range. A week with one day walked is
                worth one day, and the four lines below then add back
                up to it exactly. */}
            {[
              [`Total value (${stats.markedDays} marked entr${stats.markedDays === 1 ? 'y' : 'ies'})`,
                stats.totalValue, '#0f172a'],
              ['Amount Collected (Paid)', stats.collected, '#0f172a'],
              ...(stats.prepaid > 0.009
                ? [['Settled from advance credit', stats.prepaid, '#0f172a']] : []),
              ['Amount on Credit', stats.onCredit, '#0f172a'],
              ['Not Eaten Amount', stats.notEatenValue, '#0f172a'],
            ].map(([label, value, colour]) => (
              <Stack key={label} direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>{label}</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: colour, ...TNUM }}>
                  GH¢ {money(value)}
                </Typography>
              </Stack>
            ))}
            <Divider sx={{ mt: 0.6, mb: 0.2 }} />

            {/* The figure that keeps growing. What the dates on screen
                added is only part of it — the rest was carried in from
                the weeks before. */}
            <Box sx={{
              px: 1.5, py: 1.2, borderRadius: 2,
              bgcolor: '#fdf4e2', border: '1px solid #f3e0ba',
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <ValueIcon sx={{ fontSize: 19, color: '#c98a1c' }} />
                  <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#a16207' }}>
                      Total Outstanding (Credit)
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#a98844' }}>
                      {stats.owingStudents} learner{stats.owingStudents === 1 ? '' : 's'},
                      carried across every week
                      {stats.onCredit > 0.009
                        ? ` · GH¢ ${money(stats.onCredit)} of it added in these dates`
                        : ''}
                    </Typography>
                  </Box>
                </Stack>
                <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#a16207', ...TNUM }}>
                  GH¢ {money(stats.owingTotal)}
                </Typography>
              </Stack>
            </Box>

            {/* And its mirror: money the school is holding in advance. */}
            {stats.heldTotal > 0.009 && (
              <Box sx={{
                px: 1.5, py: 1, borderRadius: 2, mt: 0.8,
                bgcolor: '#eef0fe', border: '1px solid #c7cffb',
              }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                  <Box>
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#4338ca' }}>
                      Paid in Advance
                    </Typography>
                    <Typography sx={{ fontSize: 10, color: '#7b7fc4' }}>
                      {stats.heldStudents} learner{stats.heldStudents === 1 ? '' : 's'} —
                      the coming days are already covered
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: '#4338ca', ...TNUM }}>
                    GH¢ {money(stats.heldTotal)}
                  </Typography>
                </Stack>
              </Box>
            )}
          </Stack>
        </Card>

        <Stack spacing={1.4} sx={{ flex: '1 1 300px' }}>
          <Card elevation={0} sx={{
            p: 2, borderRadius: 3,
            bgcolor: '#f1faf5', border: '1px solid #cfe7d6',
          }}>
            <Stack direction="row" spacing={1.4} alignItems="flex-start">
              <Box sx={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                bgcolor: '#22a559', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <AddPersonIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#15803d' }}>
                  Add Others to List
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b', mb: 1.2, lineHeight: 1.6 }}>
                  Supervisor can add students who are not in the current
                  list (e.g. late arrivals, special cases).
                </Typography>
                <Button fullWidth variant="contained" disableElevation
                  onClick={() => setAddOpen(true)}
                  disabled={!className || !single}
                  sx={{
                    textTransform: 'none', fontWeight: 700, borderRadius: 1.5, py: 0.9,
                    bgcolor: '#2f6fb0', '&:hover': { bgcolor: '#1e3a6b' },
                  }}>
                  Add Student
                </Button>
                {!single && (
                  <Typography sx={{ fontSize: 10, color: '#a16207', mt: 0.6 }}>
                    Choose one collection first — a learner is added to a
                    particular collection, not to all of them at once.
                  </Typography>
                )}
              </Box>
            </Stack>
          </Card>

          <Card elevation={0} sx={{
            p: 1.8, borderRadius: 3,
            bgcolor: '#f2f7fd', border: '1px solid #dce7f5',
          }}>
            <Stack direction="row" spacing={1.2}>
              <InfoIcon sx={{ fontSize: 19, color: '#2f6fb0', mt: 0.1 }} />
              <Box>
                <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: '#1e3a6b', mb: 0.5 }}>
                  Note:
                </Typography>
                {[
                  'Only Paid and Credit students are listed for the cook.',
                  'Not Eaten students are not included in the cooking list.',
                  'A cell with an amber dot is waiting for your approval.',
                  'A dashed cell has not been submitted by the teacher yet — '
                  + 'it cannot be approved until they hand the class in.',
                  'ADVANCE means the day is already paid for out of money on '
                  + 'account. It keeps filling the days ahead, week after week, '
                  + 'until the advance runs out.',
                  'Owing is the running balance, not just these dates — it grows '
                  + 'each week a learner takes the meal on credit.',
                ].map(t => (
                  <Typography key={t} sx={{ fontSize: 11, color: '#64748b', lineHeight: 1.75 }}>
                    • {t}
                  </Typography>
                ))}
              </Box>
            </Stack>
          </Card>
        </Stack>
      </Stack>

      {isPhone && (
        <Alert severity="info" sx={{ mt: 1.4, fontSize: 12 }}>
          A week of five days is wide. Turn the phone sideways, or use
          <b> Print</b> / <b>Export</b> for a copy that fits a page.
        </Alert>
      )}

      <AddStudentDialog
        open={addOpen} onClose={() => setAddOpen(false)}
        className={className} incomeType={incomeType}
        startDate={range.from}
        defaultAmount={rows[0]?.f?.rate || 0}
        onDone={load} />

      <PrepaymentDialog
        open={!!prepayFor}
        onClose={() => setPrepayFor(null)}
        studentId={prepayFor?.student_id}
        studentName={prepayFor?.student_name}
        className={prepayFor?.class_name}
        incomeType={single ? incomeType : ''}
        onDone={load} />

      <CorrectCellDialog
        open={!!correcting}
        onClose={() => setCorrecting(null)}
        cell={correcting?.cell}
        studentId={correcting?.studentId}
        studentName={correcting?.studentName}
        className={correcting?.className}
        incomeType={correcting?.incomeType}
        date={correcting?.date}
        rate={correcting?.rate}
        onDone={load} />

      <WriteOffDialog
        student={writeOffFor}
        onClose={() => setWriteOffFor(null)}
        onDone={load} />

      <WriteOffHistoryDialog
        open={historyOpen} onClose={() => setHistoryOpen(false)}
        from={range.from} to={range.to}
        className={className} incomeType={incomeType}
        onChanged={load} />

      <OutstandingDialog
        open={outstandingOpen}
        onClose={() => setOutstandingOpen(false)}
        todayIso={todayIso}
        onChanged={() => { load(); refreshOutstandingCount(); }} />
    </Box>
  );
}

// ═══ Everything still waiting, wherever it is ══════════════════════
//
// One class, one week is the right shape for WALKING a round. It is the
// wrong shape for finding the entries nobody ever ruled on: they sit in
// whatever week they were collected, and the only clue anything is
// wrong is a number climbing in the sidebar.
//
// So this ignores the sheet's filters entirely and asks for the lot,
// grouped by the day the money was taken, OLDEST FIRST — because the
// oldest is the one the collector has been carrying longest and the one
// a parent is most likely to ask about.
function OutstandingDialog({ open, onClose, todayIso, onChanged }) {
  const [rows, setRows]   = useState([]);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');
  const [picked, setPicked] = useState([]);

  const load = useCallback(async () => {
    if (!open) return;
    setBusy(true); setErr('');
    try {
      // No date filter: that is the whole point.
      const r = await API.get('/collections/pending');
      const all = Array.isArray(r.data) ? r.data : (r.data?.rows || []);
      // Today's already-decided entries come back too, so the approver
      // can see what they just did. Not what this dialog is for.
      setRows(all.filter(x => String(x.status || '').toUpperCase() === 'PENDING'));
      setPicked([]);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not load the outstanding entries');
      setRows([]);
    } finally { setBusy(false); }
  }, [open]);
  useEffect(() => { load(); }, [load]);

  // Grouped by the day the money was taken, oldest first.
  const days = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const d = String(r.payment_date || '').slice(0, 10);
      if (!m.has(d)) m.set(d, []);
      m.get(d).push(r);
    }
    return [...m.entries()]
      .map(([day, list]) => ({
        day,
        list: list.sort((a, b) =>
          String(a.class_name || '').localeCompare(String(b.class_name || ''))
          || String(a.student_name || '').localeCompare(String(b.student_name || ''))),
        total: list.reduce((s, x) => s + Number(x.net_amount || 0), 0),
        stale: day < todayIso,
      }))
      .sort((a, b) => a.day.localeCompare(b.day));
  }, [rows, todayIso]);

  const pickedSet = useMemo(() => new Set(picked), [picked]);
  const toggle = (id) =>
    setPicked(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const toggleDay = (d) => {
    const ids = d.list.map(x => x.collection_id);
    const allOn = ids.every(i => pickedSet.has(i));
    setPicked(p => allOn ? p.filter(i => !ids.includes(i)) : [...new Set([...p, ...ids])]);
  };

  const pickedTotal = useMemo(
    () => rows.filter(r => pickedSet.has(r.collection_id))
              .reduce((s, r) => s + Number(r.net_amount || 0), 0),
    [rows, pickedSet]);

  const approve = async (ids, what) => {
    if (!ids.length || busy) return;
    const total = rows.filter(r => ids.includes(r.collection_id))
                      .reduce((s, r) => s + Number(r.net_amount || 0), 0);
    const ok = await swal.confirm({
      title: `Approve ${what}?`,
      tone: 'success',
      html: `<b>${ids.length}</b> entr${ids.length === 1 ? 'y' : 'ies'} `
          + `&middot; <b>GH¢ ${money(total)}</b><br/>`
          + `<span style="opacity:.75">This posts the money — receipts and cash book.</span>`,
      confirmText: 'Yes, approve',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await API.post('/collections/bulk-approve', { collection_ids: ids });
      swal.success(r.data?.message || `Approved ${ids.length}`);
      await load();
      onChanged?.();
      window.dispatchEvent(new Event('pendingCountsChanged'));
    } catch (e) { swal.error(swal.pickError(e, 'Could not approve')); }
    finally { setBusy(false); }
  };

  // Rejecting is the answer for an entry that should never have been
  // raised. Nothing was posted, so nothing has to be unposted — the row
  // is simply declined, with a reason, and the money never enters the
  // books. It is NOT the same as writing a balance off, and the note in
  // the dialog says so, because choosing the wrong one of those two
  // leaves either a phantom debt or a phantom receipt.
  const reject = async (ids, what) => {
    if (!ids.length || busy) return;
    const reason = await swal.prompt({
      title: `Reject ${what}?`,
      text: 'Say why. The collector sees this, and it stays on the record.',
      inputType: 'textarea',
      inputPlaceholder: 'e.g. entered against the wrong learner',
      confirmText: 'Reject',
      // The backend refuses anything under five characters — caught here
      // so the approver is told before the first request, not after the
      // last one in a loop.
      validate: (v) => (String(v || '').trim().length < 5
        ? 'Give a reason of at least 5 characters' : undefined),
    });
    if (!reason) return;
    setBusy(true);
    let ok = 0, failed = 0;
    for (const id of ids) {
      try { await API.post(`/collections/${id}/reject`, { reason: String(reason).trim() }); ok++; }
      catch { failed++; }
    }
    setBusy(false);
    if (ok) swal.success(`Rejected ${ok}${failed ? ` · ${failed} could not be rejected` : ''}`);
    else swal.error('Nothing could be rejected');
    await load();
    onChanged?.();
    window.dispatchEvent(new Event('pendingCountsChanged'));
  };

  const grand = rows.reduce((s, r) => s + Number(r.net_amount || 0), 0);
  const staleDays = days.filter(d => d.stale);

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 16 }}>
        Awaiting approval
        <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
          {rows.length} entr{rows.length === 1 ? 'y' : 'ies'} · GH¢ {money(grand)}
          {staleDays.length > 0 && ` · ${staleDays.length} day${staleDays.length === 1 ? '' : 's'} before today`}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12.5 }}>{err}</Alert>}

        <Alert severity="info" variant="outlined" sx={{ mb: 1.5, fontSize: 12 }}>
          <b>Approve</b> posts the money — a receipt is written and the cash book takes it.
          <b> Reject</b> declines the entry; nothing is posted, so nothing has to be undone.
          Neither one clears a learner's arrears — that is <b>Write off</b>, back on the sheet.
        </Alert>

        {busy && rows.length === 0 && (
          <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        )}
        {!busy && rows.length === 0 && (
          <Alert severity="success" sx={{ fontSize: 12.5 }}>
            Nothing is waiting. Every entry has been ruled on.
          </Alert>
        )}

        {days.map(d => {
          const ids = d.list.map(x => x.collection_id);
          const allOn = ids.every(i => pickedSet.has(i));
          return (
            <Box key={d.day} sx={{ mb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={1}
                sx={{
                  px: 1.4, py: 0.9, borderRadius: 1.5, mb: 0.6, flexWrap: 'wrap', gap: 0.8,
                  bgcolor: d.stale ? '#fffbf3' : '#f8fafc',
                  border: '1px solid', borderColor: d.stale ? '#fcd9a6' : '#e2e8f0',
                }}>
                <Checkbox size="small" checked={allOn}
                  indeterminate={!allOn && ids.some(i => pickedSet.has(i))}
                  onChange={() => toggleDay(d)} sx={{ p: 0.3 }} />
                <Typography sx={{ fontWeight: 800, fontSize: 13 }}>
                  {longDate(d.day)}
                </Typography>
                {/* The thing the approver most needs to notice. */}
                {d.stale && (
                  <Chip size="small" label="not today"
                    sx={{ height: 19, fontSize: 10, fontWeight: 800,
                      bgcolor: '#fef3e2', color: '#92400e' }} />
                )}
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                  {d.list.length} entr{d.list.length === 1 ? 'y' : 'ies'} · GH¢ {money(d.total)}
                </Typography>
                <Box sx={{ flex: 1 }} />
                <Button size="small" disabled={busy}
                  onClick={() => approve(ids, longDate(d.day))}
                  sx={{ textTransform: 'none', fontWeight: 800, color: '#15803d' }}>
                  Approve day
                </Button>
                <Button size="small" disabled={busy}
                  onClick={() => reject(ids, longDate(d.day))}
                  sx={{ textTransform: 'none', fontWeight: 800, color: '#b91c1c' }}>
                  Reject day
                </Button>
              </Stack>

              <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f8fafc' }}>
                      <TableCell sx={{ width: 40 }} />
                      <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>Class</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>Learner</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>Collection</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: 11 }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>Collector</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 800, fontSize: 11, width: 150 }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {d.list.map(r => (
                      <TableRow key={r.collection_id} hover>
                        <TableCell>
                          <Checkbox size="small" sx={{ p: 0.3 }}
                            checked={pickedSet.has(r.collection_id)}
                            onChange={() => toggle(r.collection_id)} />
                        </TableCell>
                        <TableCell sx={{ fontSize: 11.5 }}>{r.class_name}</TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>
                          {r.student_name || r.student_id}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11.5 }}>{prettyType(r.income_type)}</TableCell>
                        <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 800, ...TNUM }}>
                          {money(r.net_amount)}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>{r.collector_name || '—'}</TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                          <Button size="small" disabled={busy}
                            onClick={() => approve([r.collection_id], r.student_name || 'this entry')}
                            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 11, color: '#15803d' }}>
                            Approve
                          </Button>
                          <Button size="small" disabled={busy}
                            onClick={() => reject([r.collection_id], r.student_name || 'this entry')}
                            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 11, color: '#b91c1c' }}>
                            Reject
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          );
        })}
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {picked.length > 0 && (
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#475569', mr: 'auto', pl: 1 }}>
            {picked.length} selected · GH¢ {money(pickedTotal)}
          </Typography>
        )}
        <Button onClick={onClose} disabled={busy}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Close
        </Button>
        {picked.length > 0 && (
          <>
            <Button variant="outlined" color="error" disabled={busy}
              onClick={() => reject(picked, `${picked.length} selected`)}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Reject selected
            </Button>
            <Button variant="contained" color="success" disableElevation disabled={busy}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <ApproveIcon />}
              onClick={() => approve(picked, `${picked.length} selected`)}
              sx={{ textTransform: 'none', fontWeight: 800 }}>
              Approve selected ({picked.length})
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ═══ Writing a balance off ═════════════════════════════════════════
//
// Arrears a learner does not owe, or credit the school never received.
// Both usually come from the same thing: a day marked against the wrong
// name, or marked for a child who was not in school.
//
// The dialog shows every collection the learner is on with a balance,
// because a learner can be on feeding AND transport and the mistake is
// only on one of them. Writing off the wrong one would leave the real
// error in place and create a second one.
function WriteOffDialog({ student, onClose, onDone }) {
  const open = !!student;
  const [pick, setPick]     = useState('');     // "income_type|kind"
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState('');

  // Every side of every collection that actually carries a balance.
  const options = useMemo(() => {
    if (!student) return [];
    const out = [];
    for (const t of Object.keys(student.types || {})) {
      const row = student.types[t] || {};
      if (Number(row.arrears) > 0.009) {
        out.push({ key: `${t}|arrears`, income_type: t, kind: 'arrears',
                   available: Number(row.arrears) });
      }
      if (Number(row.credit) > 0.009) {
        out.push({ key: `${t}|credit`, income_type: t, kind: 'credit',
                   available: Number(row.credit) });
      }
    }
    return out;
  }, [student]);

  // Land on the only option when there is only one — the common case,
  // and one less decision to make.
  useEffect(() => {
    if (!open) { setPick(''); setAmount(''); setReason(''); setErr(''); return; }
    const only = options.length === 1 ? options[0] : null;
    setPick(only ? only.key : '');
    setAmount(only ? String(money(only.available)) : '');
    setReason(''); setErr('');
  }, [open, options]);

  const chosen = options.find(o => o.key === pick) || null;

  const choose = (key) => {
    setPick(key);
    const o = options.find(x => x.key === key);
    // Prefilled with the WHOLE balance: clearing all of it is the usual
    // intent, and retyping a figure already on screen only invites a
    // typo. Editable for the case where only part of it was wrong.
    setAmount(o ? String(money(o.available)) : '');
    setErr('');
  };

  const submit = async () => {
    if (!chosen) { setErr('Pick which balance to write off.'); return; }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setErr('Enter an amount greater than 0.'); return; }
    if (amt > chosen.available + 0.009) {
      setErr(`Only GH¢ ${money(chosen.available)} is outstanding.`); return;
    }
    if (reason.trim().length < 3) {
      setErr('Give a reason — it is the only record of why the balance changed.'); return;
    }
    setBusy(true); setErr('');
    try {
      const r = await API.post('/collections/write-offs', {
        student_id:  student.student_id,
        income_type: chosen.income_type,
        kind:        chosen.kind,
        amount:      amt,
        reason:      reason.trim(),
      });
      swal.success(r.data?.message || 'Balance written off');
      onClose();
      onDone?.();
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not write the balance off');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 16 }}>
        Write off a balance
        <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
          {student?.student_name} · {student?.class_name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12.5 }}>{err}</Alert>}

        {options.length === 0 ? (
          <Alert severity="info" sx={{ fontSize: 12.5 }}>
            This learner has no arrears and no credit — there is nothing to write off.
          </Alert>
        ) : (
          <>
            <Typography sx={{ fontSize: 12, color: '#475569', mb: 1 }}>
              This removes the balance and records who did it and why. It can be
              reversed from <b>Write-offs</b>.
            </Typography>

            <Stack spacing={0.8} sx={{ mb: 1.8 }}>
              {options.map(o => {
                const on = pick === o.key;
                const isArrears = o.kind === 'arrears';
                return (
                  <Box key={o.key} component="button" type="button"
                    onClick={() => choose(o.key)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1,
                      width: '100%', textAlign: 'left', cursor: 'pointer',
                      px: 1.4, py: 1, borderRadius: 1.5, font: 'inherit',
                      border: '1px solid',
                      borderColor: on ? (isArrears ? '#f0a5a5' : '#a5c8f0') : '#e2e8f0',
                      bgcolor: on ? (isArrears ? '#fdf2f2' : '#f2f7fd') : '#fff',
                    }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: '#0f172a' }}>
                        {prettyType(o.income_type)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700,
                        color: isArrears ? '#b91c1c' : '#1d4ed8' }}>
                        {isArrears
                          ? 'Arrears — the learner owes this'
                          : 'Credit — the school is holding this for them'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: 15, fontWeight: 800, ...TNUM,
                      color: isArrears ? '#b91c1c' : '#1d4ed8' }}>
                      GH¢ {money(o.available)}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} sx={{ mb: 1.5 }}>
              <TextField size="small" type="number" label="Amount to write off (GH¢)"
                value={amount} onChange={e => setAmount(e.target.value)}
                disabled={!chosen || busy}
                inputProps={{ min: 0, step: '0.01' }}
                helperText={chosen
                  ? `Whole balance is GH¢ ${money(chosen.available)} — change it to write off part`
                  : 'Pick a balance above first'}
                sx={{ flex: 1 }} />
            </Stack>

            <TextField size="small" fullWidth multiline minRows={2}
              label="Reason (required)"
              placeholder="e.g. Marked in error — the child was absent all week"
              value={reason} onChange={e => setReason(e.target.value)}
              disabled={busy}
              helperText="Stored against the write-off. It is the only record of why this balance changed." />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Cancel
        </Button>
        <Button variant="contained" color="warning" onClick={submit}
          disabled={busy || options.length === 0 || !chosen}
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <WriteOffIcon />}
          sx={{ textTransform: 'none', fontWeight: 800 }}>
          {chosen ? `Write off GH¢ ${money(Number(amount) || 0)}` : 'Write off'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══ What has been written off ═════════════════════════════════════
//
// Scoped to the dates, class and collection the sheet is showing, so it
// answers "what was taken off THIS sheet" rather than everything the
// school has ever written off. A reversed row stays in the list marked
// as reversed — "written off then put back" is itself worth seeing.
function WriteOffHistoryDialog({ open, onClose, from, to, className, incomeType, onChanged }) {
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [err, setErr]     = useState('');
  const [scope, setScope] = useState('range');   // 'range' | 'all'

  const load = useCallback(async () => {
    if (!open) return;
    setBusy(true); setErr('');
    try {
      const params = scope === 'range'
        ? { from, to,
            ...(className ? { class_name: className } : {}),
            ...(incomeType ? { income_type: incomeType } : {}) }
        : {};
      const r = await API.get('/collections/write-offs', { params });
      setData(r.data || null);
    } catch (e) {
      setErr(e?.response?.data?.message || 'Could not load the write-offs');
      setData(null);
    } finally { setBusy(false); }
  }, [open, scope, from, to, className, incomeType]);

  useEffect(() => { load(); }, [load]);

  const undo = async (row) => {
    const ok = await swal.confirm({
      title: 'Put this balance back?',
      text: `GH¢ ${money(row.amount)} of ${row.kind} will be added back to `
          + `${row.student_name || row.student_id} on ${prettyType(row.income_type)}.`,
      confirmText: 'Yes, put it back',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await API.post(`/collections/write-offs/${row.id}/reverse`, {});
      swal.success(r.data?.message || 'Balance restored');
      await load();
      onChanged?.();
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not reverse the write-off'));
    } finally { setBusy(false); }
  };

  const rows = data?.rows || [];

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ fontWeight: 800, fontSize: 16 }}>
        Write-offs
        <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 600 }}>
          {scope === 'range'
            ? `${rangeLabel(from, to)}${className ? ` · ${className}` : ''}`
              + `${incomeType ? ` · ${prettyType(incomeType)}` : ''}`
            : 'Every write-off on record'}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 1.5, fontSize: 12.5 }}>{err}</Alert>}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
          <Button size="small" variant={scope === 'range' ? 'contained' : 'outlined'}
            onClick={() => setScope('range')}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            These dates
          </Button>
          <Button size="small" variant={scope === 'all' ? 'contained' : 'outlined'}
            onClick={() => setScope('all')}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            All time
          </Button>
          <Box sx={{ flex: 1 }} />
          {data?.totals && (
            <>
              {data.totals.arrears > 0.009 && (
                <Chip size="small" label={`Arrears written off: GH¢ ${money(data.totals.arrears)}`}
                  sx={{ fontWeight: 700, bgcolor: '#fdf2f2', color: '#b91c1c' }} />
              )}
              {data.totals.credit > 0.009 && (
                <Chip size="small" label={`Credit written off: GH¢ ${money(data.totals.credit)}`}
                  sx={{ fontWeight: 700, bgcolor: '#f2f7fd', color: '#1d4ed8' }} />
              )}
              {data.totals.reversed > 0 && (
                <Chip size="small" variant="outlined"
                  label={`${data.totals.reversed} reversed`} sx={{ fontWeight: 700 }} />
              )}
            </>
          )}
        </Stack>

        {busy && rows.length === 0 && (
          <Box sx={{ py: 5, textAlign: 'center' }}><CircularProgress size={22} /></Box>
        )}
        {!busy && rows.length === 0 && (
          <Alert severity="info" sx={{ fontSize: 12.5 }}>
            Nothing has been written off{scope === 'range' ? ' in these dates' : ''}.
          </Alert>
        )}

        {rows.length > 0 && (
          <TableContainer sx={{ border: '1px solid #e2e8f0', borderRadius: 1.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>When</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>Learner</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>Collection</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>Took off</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: 11 }}>Amount</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>Reason</TableCell>
                  <TableCell sx={{ fontWeight: 800, fontSize: 11 }}>By</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: 11, width: 96 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map(r => {
                  const gone = !!r.reversed_at;
                  return (
                    <TableRow key={r.id} sx={{ opacity: gone ? 0.55 : 1 }}>
                      <TableCell sx={{ fontSize: 11.5, whiteSpace: 'nowrap' }}>
                        {String(r.created_at || '').slice(0, 16).replace('T', ' ')}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12, fontWeight: 700 }}>
                        {r.student_name || r.student_id}
                        <Typography sx={{ fontSize: 10, color: '#94a3b8' }}>
                          {r.class_name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 11.5 }}>{prettyType(r.income_type)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={r.kind}
                          sx={{ height: 18, fontSize: 9.5, fontWeight: 800,
                            bgcolor: r.kind === 'arrears' ? '#fdf2f2' : '#f2f7fd',
                            color:   r.kind === 'arrears' ? '#b91c1c' : '#1d4ed8' }} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontSize: 12.5, fontWeight: 800, ...TNUM }}>
                        {money(r.amount)}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11.5, color: '#475569', maxWidth: 240 }}>
                        {r.reason}
                        {gone && (
                          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#b45309' }}>
                            Reversed by {r.reversed_by_name || 'someone'}
                            {r.reverse_reason ? ` — ${r.reverse_reason}` : ''}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontSize: 11 }}>{r.created_by_name || '—'}</TableCell>
                      <TableCell align="right">
                        {gone
                          ? <Chip size="small" variant="outlined" label="Reversed"
                              sx={{ height: 20, fontSize: 9.5, fontWeight: 700 }} />
                          : (
                            <Button size="small" onClick={() => undo(r)} disabled={busy}
                              sx={{ textTransform: 'none', fontWeight: 700, fontSize: 11 }}>
                              Put back
                            </Button>
                          )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}
          sx={{ textTransform: 'none', fontWeight: 700 }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ═══ Putting a learner onto this collection ════════════════════════
//
// The list offered is learners in the class who are NOT already on this
// collection — /eligible-students answers exactly that, so a duplicate
// subscription cannot be created from here.
function AddStudentDialog({ open, onClose, className, incomeType, startDate, defaultAmount, onDone }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState('');
  const [chosen, setChosen] = useState([]);
  const [amount, setAmount] = useState('');

  useEffect(() => {
    if (!open || !incomeType) return;
    setChosen([]); setQ(''); setAmount(String(Number(defaultAmount || 0).toFixed(2)));
    setBusy(true);
    API.get('/daily-subscriptions/eligible-students',
      { params: { income_type: incomeType, cla: className } })
      .then(r => setList(Array.isArray(r.data) ? r.data : []))
      .catch(e => swal.error(swal.pickError(e, 'Could not load the students')))
      .finally(() => setBusy(false));
  }, [open, incomeType, className, defaultAmount]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return list;
    return list.filter(s => `${s.name} ${s.index_no}`.toLowerCase().includes(needle));
  }, [list, q]);

  const save = async () => {
    if (!chosen.length) { swal.warning('Pick at least one student.'); return; }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      swal.warning('Enter the daily amount to charge.'); return;
    }
    setBusy(true);
    try {
      const r = await API.post('/daily-subscriptions/bulk', {
        income_type: incomeType,
        start_date: startDate,
        default_amount: amt,
        students: chosen.map(id => ({ student_id: id, daily_amount: amt })),
      });
      swal.success(r.data?.message || `${chosen.length} added`);
      onClose(); onDone?.();
    } catch (e) {
      // Registering learners is an admin permission; an approver who
      // lacks it should be told so, not handed a bare 403.
      const status = e?.response?.status;
      swal.error(status === 403
        ? 'You do not have permission to register students onto a collection. '
          + 'Ask an administrator, or use Collections → Registration.'
        : swal.pickError(e, 'Could not add the students'));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 800, color: '#1e3a6b', fontSize: 17 }}>
        Add to {prettyType(incomeType)}
        <Typography sx={{ fontSize: 12, color: '#64748b', fontWeight: 500 }}>
          {className} · starts {startDate} · only learners not already on this collection
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Stack direction="row" spacing={1} sx={{ mb: 1.4 }}>
          <TextField size="small" fullWidth placeholder="Search name or index number"
            value={q} onChange={(e) => setQ(e.target.value)}
            InputProps={{ startAdornment: (
              <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
            ) }} />
          <TextField size="small" label="Daily amount" type="number" value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }} sx={{ width: 148 }} />
        </Stack>

        {busy && <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={26} /></Box>}

        {!busy && filtered.length === 0 && (
          <Typography sx={{ py: 3, textAlign: 'center', color: '#64748b', fontSize: 13 }}>
            Every learner in {className || 'this class'} is already on this collection.
          </Typography>
        )}

        {!busy && filtered.map(s => (
          <Stack key={s.index_no} direction="row" spacing={1} alignItems="center"
            onClick={() => setChosen(c =>
              c.includes(s.index_no) ? c.filter(x => x !== s.index_no) : [...c, s.index_no])}
            sx={{
              px: 1, py: 0.5, borderRadius: 1.5, cursor: 'pointer',
              '&:hover': { bgcolor: '#f1f6fd' },
            }}>
            <Checkbox size="small" checked={chosen.includes(s.index_no)} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                {s.name}
              </Typography>
              <Typography sx={{ fontSize: 10.5, color: '#94a3b8', ...TNUM }}>
                {s.index_no} · {s.class_name}
              </Typography>
            </Box>
          </Stack>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button variant="contained" disableElevation onClick={save}
          disabled={busy || !chosen.length}
          sx={{
            textTransform: 'none', fontWeight: 700,
            bgcolor: '#22a559', '&:hover': { bgcolor: '#15803d' },
          }}>
          Add {chosen.length || ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
